export interface NotificationPreferences {
  email: boolean;
  sms: boolean;
}

export type AccountType = 'consumer' | 'business';

export interface Account {
  id: string;
  sonic_account_number?: string;
  status: 'active' | 'inactive' | 'suspended';
  organisation_name?: string;
  contact_first_name?: string;
  contact_last_name?: string;
  mobile_number?: string;
  email?: string;
  main_phone?: string;
  fax?: string;
  website?: string;
  address_line1?: string;
  address_line2?: string;
  address_suburb?: string;
  address_state?: string;
  address_postcode?: string;
  address_country?: string;
  address_formatted?: string;
  account_type?: AccountType;
  abn?: string;
  date_of_birth?: string;
  notification_preferences: NotificationPreferences;
  created_at: string;
  updated_at: string;
  last_notified_at?: string;
  full_name: string;
  display_name: string;
  service_count: number;
}

export interface AccountCreate {
  sonic_account_number?: string;
  status?: 'active' | 'inactive' | 'suspended';
  organisation_name?: string;
  contact_first_name?: string;
  contact_last_name?: string;
  mobile_number?: string;
  email?: string;
  main_phone?: string;
  fax?: string;
  website?: string;
  address_line1?: string;
  address_line2?: string;
  address_suburb?: string;
  address_state?: string;
  address_postcode?: string;
  address_country?: string;
  address_formatted?: string;
  account_type?: AccountType;
  abn?: string;
  date_of_birth?: string;
  notification_preferences?: NotificationPreferences;
}

export interface AccountUpdate {
  sonic_account_number?: string;
  status?: 'active' | 'inactive' | 'suspended';
  organisation_name?: string;
  contact_first_name?: string;
  contact_last_name?: string;
  mobile_number?: string;
  email?: string;
  main_phone?: string;
  fax?: string;
  website?: string;
  address_line1?: string;
  address_line2?: string;
  address_suburb?: string;
  address_state?: string;
  address_postcode?: string;
  address_country?: string;
  address_formatted?: string;
  account_type?: AccountType;
  abn?: string;
  date_of_birth?: string;
  notification_preferences?: NotificationPreferences;
}

export interface AccountWithServices extends Account {
  services: AccountService[];
}

/**
 * field_keys that collide with hardcoded AccountService columns (see
 * backend/app/models/account_service.py). Hidden from the generic "dynamic
 * fields" UI (built from service_type_fields/type_specific_data) because
 * these are edited via the dedicated hardcoded-field sections instead.
 * Keep in sync with account_service.py's HARDCODED_ACCOUNT_SERVICE_FIELD_KEYS.
 */
export const HARDCODED_ACCOUNT_SERVICE_FIELD_KEYS = new Set<string>([
  'wsm_service_id', 'nbn_carrier_id', 'nbn_vanity_identifier', 'nbn_avc', 'nbn_poi',
  'nbn_plan_id', 'nbn_technology_type', 'nbn_authentication_type', 'nbn_ip_address_type',
  'nbn_ip_address', 'nbn_service_class', 'nbn_sla', 'nbn_mapping', 'nbn_unid_port',
  'primary_domain', 'web_server', 'web_hosting_plan', 'panel_type',
]);

export interface AccountService {
  id: string;
  account_id: string;
  service_id: string;
  /** Code from Settings → Service Types (`service_types.code`), e.g. nbn, sonweb, virtual_machine */
  service_type: string;
  wsm_service_id?: string;
  status: 'active' | 'inactive' | 'suspended';
  upstream_carrier?: string;
  notes?: string;
  nbn_carrier_id?: string;
  nbn_vanity_identifier?: string;
  nbn_avc?: string;
  nbn_poi?: string;
  nbn_plan_id?: string;
  nbn_technology_type?: string;
  nbn_authentication_type?: string;
  nbn_ip_address_type?: string;
  nbn_ip_address?: string;
  nbn_service_class?: string;
  nbn_sla?: string;
  nbn_mapping?: string;
  nbn_unid_port?: string;
  primary_domain?: string;
  web_server?: string;
  web_hosting_plan?: string;
  panel_type?: string;
  /** Values for Settings → Service Types custom fields (keyed by field_key). */
  type_specific_data?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  /** Set by search/report API */
  account_display_name?: string;
  /** Resolved plan name (includes grandfathered/inactive NBN plans). Set by API. */
  nbn_plan_name?: string;
  /** Display name from Settings → Service Types (`service_types.name`) for this `service_type` code. */
  service_type_name?: string | null;
}

export interface AccountServiceCreate {
  account_id: string;
  service_id: string;
  /** Code from Settings → Service Types (`service_types.code`), e.g. nbn, sonweb, virtual_machine */
  service_type: string;
  wsm_service_id?: string;
  status?: 'active' | 'inactive' | 'suspended';
  upstream_carrier?: string;
  notes?: string;
  nbn_carrier_id?: string;
  nbn_vanity_identifier?: string;
  nbn_avc?: string;
  nbn_poi?: string;
  nbn_plan_id?: string;
  nbn_technology_type?: string;
  nbn_authentication_type?: string;
  nbn_ip_address_type?: string;
  nbn_ip_address?: string;
  nbn_service_class?: string;
  nbn_sla?: string;
  nbn_mapping?: string;
  nbn_unid_port?: string;
  primary_domain?: string;
  web_server?: string;
  web_hosting_plan?: string;
  panel_type?: string;
  type_specific_data?: Record<string, unknown>;
}

export interface AccountServiceUpdate {
  service_id?: string;
  service_type?: string;
  wsm_service_id?: string;
  status?: 'active' | 'inactive' | 'suspended';
  upstream_carrier?: string;
  notes?: string;
  nbn_carrier_id?: string;
  nbn_vanity_identifier?: string;
  nbn_avc?: string;
  nbn_poi?: string;
  nbn_plan_id?: string;
  nbn_technology_type?: string;
  nbn_authentication_type?: string;
  nbn_ip_address_type?: string;
  nbn_ip_address?: string;
  nbn_service_class?: string;
  nbn_sla?: string;
  nbn_mapping?: string;
  nbn_unid_port?: string;
  primary_domain?: string;
  web_server?: string;
  web_hosting_plan?: string;
  panel_type?: string;
  type_specific_data?: Record<string, unknown>;
}

export interface AccountAsset {
  id: string;
  account_id: string;
  asset_name: string;
  asset_type?: string | null;
  manufacturer?: string | null;
  model_number?: string | null;
  status?: string | null;
  serial_number?: string | null;
  mac_address?: string | null;
  distributor?: string | null;
  sonic_warranty_item?: boolean;
  distributor_reference?: string | null;
  purchase_date?: string | null;
  manufacturer_warranty_expiry?: string | null;
  extended_warranty_expiry?: string | null;
  notes?: string | null;
  /** Router asset_type only */
  portal_ip_address?: string | null;
  portal_user?: string | null;
  portal_password?: string | null;
  wifi_ssid?: string | null;
  wifi_password?: string | null;
  network_band_24ghz?: boolean;
  network_band_5ghz?: boolean;
  network_band_6ghz?: boolean;
  /** Router + TP-Link TAUC management */
  tplink_tauc_managed?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface AccountAssetCreate {
  account_id: string;
  asset_name: string;
  asset_type?: string | null;
  manufacturer?: string | null;
  model_number?: string | null;
  status?: string | null;
  serial_number?: string | null;
  mac_address?: string | null;
  distributor?: string | null;
  sonic_warranty_item?: boolean;
  distributor_reference?: string | null;
  purchase_date?: string | null;
  manufacturer_warranty_expiry?: string | null;
  extended_warranty_expiry?: string | null;
  notes?: string | null;
  portal_ip_address?: string | null;
  portal_user?: string | null;
  portal_password?: string | null;
  wifi_ssid?: string | null;
  wifi_password?: string | null;
  network_band_24ghz?: boolean;
  network_band_5ghz?: boolean;
  network_band_6ghz?: boolean;
  tplink_tauc_managed?: boolean;
}

export interface AccountAssetUpdate {
  asset_name?: string;
  asset_type?: string | null;
  manufacturer?: string | null;
  model_number?: string | null;
  status?: string | null;
  serial_number?: string | null;
  mac_address?: string | null;
  distributor?: string | null;
  sonic_warranty_item?: boolean;
  distributor_reference?: string | null;
  purchase_date?: string | null;
  manufacturer_warranty_expiry?: string | null;
  extended_warranty_expiry?: string | null;
  notes?: string | null;
  portal_ip_address?: string | null;
  portal_user?: string | null;
  portal_password?: string | null;
  wifi_ssid?: string | null;
  wifi_password?: string | null;
  network_band_24ghz?: boolean;
  network_band_5ghz?: boolean;
  network_band_6ghz?: boolean;
  tplink_tauc_managed?: boolean;
}

export interface AccountListResponse {
  accounts: Account[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export interface AccountServiceListResponse {
  services: AccountService[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export interface AccountStats {
  total_accounts: number;
  active_accounts: number;
  by_status: Record<string, number>;
  with_email: number;
  with_mobile: number;
  recent_additions: number;
}

export interface AccountSearchFilters {
  q?: string;
  status?: 'active' | 'inactive' | 'suspended';
  has_email?: boolean;
  has_mobile?: boolean;
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export interface AccountServiceSearchFilters {
  q?: string;
  account_id?: string;
  service_type?: string;
  status?: 'active' | 'inactive' | 'suspended';
  nbn_carrier_id?: string;
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

// Account contacts (primary from account + additional with type secondary, billing, admin)
export type AccountContactType = 'primary' | 'secondary' | 'billing' | 'admin' | 'technical';

export interface AccountContact {
  id: string;
  account_id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  main_phone?: string;
  mobile_number?: string;
  date_of_birth?: string;
  contact_type: AccountContactType;
  full_name: string;
  created_at?: string;
  updated_at?: string;
}

export interface AccountContactCreate {
  first_name?: string;
  last_name?: string;
  email?: string;
  main_phone?: string;
  mobile_number?: string;
  date_of_birth?: string;
  contact_type: AccountContactType;
}

export interface AccountContactUpdate {
  first_name?: string;
  last_name?: string;
  email?: string;
  main_phone?: string;
  mobile_number?: string;
  date_of_birth?: string;
  contact_type?: AccountContactType;
}

export interface AccountDocument {
  id: string;
  account_id: string | null;
  order_id: string | null;
  document_source: 'system' | 'manual';
  category: string;
  name: string;
  description?: string | null;
  filename: string;
  content_type?: string | null;
  file_size_bytes?: number | null;
  uploaded_by_id?: string | null;
  uploaded_by_name?: string | null;
  created_at: string;
  updated_at: string;
}
